import { useEffect, useRef, useState } from 'react';
import { Package, Upload, Save, RefreshCw, AlertCircle, Smartphone } from 'lucide-react';
import { api } from '../../services/api';
import toast from '../Toast';
import { formatBeijingDateTime } from '../../utils/time';

interface ReleaseState {
  versionCode: number | null;
  versionName: string;
  notes: string;
  force: boolean;
  apk: { exists: boolean; size: number; updatedAt: string | null };
  apkVersion: { versionCode: number; versionName: string } | null;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function AppReleaseTab() {
  const [state, setState] = useState<ReleaseState | null>(null);
  const [loading, setLoading] = useState(true);

  const [versionCode, setVersionCode] = useState('');
  const [versionName, setVersionName] = useState('');
  const [notes, setNotes] = useState('');
  const [force, setForce] = useState(false);
  const [saving, setSaving] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getAppRelease();
      setState(data);
      setVersionCode(data.versionCode ? String(data.versionCode) : '');
      setVersionName(data.versionName || '');
      setNotes(data.notes || '');
      setForce(data.force);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '获取发布信息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSave = async () => {
    const code = Number(versionCode);
    if (!Number.isInteger(code) || code <= 0) {
      toast.error('versionCode 必须是正整数');
      return;
    }
    if (!versionName.trim()) {
      toast.error('版本名称不能为空');
      return;
    }

    setSaving(true);
    try {
      await api.saveAppRelease({
        versionCode: code,
        versionName: versionName.trim(),
        notes,
        force,
      });
      toast.success('已保存，App 下次检查就会拿到新版本信息');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.apk')) {
      toast.error('请选择 .apk 文件');
      return;
    }

    setUploading(true);
    try {
      const result = await api.uploadAppApk(file);
      toast.success(result.message);

      // 用包里的真实版本回填，避免填错
      if (result.apkVersion) {
        setVersionCode(String(result.apkVersion.versionCode));
        setVersionName(result.apkVersion.versionName);
      }

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  if (loading && !state) {
    return (
      <div className="card p-8 flex items-center justify-center text-sm text-light-muted">
        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
        正在加载发布信息…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-light-text flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-accent-blue" />
              App 发布
            </h2>
            <p className="text-sm text-light-muted mt-1">
              改完保存即可生效，不需要发后端；App 启动和手动检查都会读这里的配置。
            </p>
          </div>
          <button
            onClick={() => void load()}
            className="btn-secondary text-sm flex items-center gap-1.5"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-light-text mb-1.5">
              版本号（versionCode）
            </label>
            <input
              type="number"
              value={versionCode}
              onChange={(e) => setVersionCode(e.target.value)}
              className="input-field w-full"
              placeholder="例如 2003"
            />
            <p className="text-xs text-light-muted mt-1">
              必须大于 App 当前安装的构建号才会触发更新；需与安装包一致
            </p>
            {state?.apkVersion && Number(versionCode) !== state.apkVersion.versionCode && (
              <p className="text-xs text-red-500 mt-1">
                安装包实际是 v{state.apkVersion.versionName}（{state.apkVersion.versionCode}），填写的与它不一致会被拒绝保存
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-light-text mb-1.5">
              版本名称（versionName）
            </label>
            <input
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              className="input-field w-full"
              placeholder="例如 1.0.2"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-light-text mb-1.5">更新说明</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="input-field w-full resize-y"
            placeholder="会显示在 App 的更新弹窗里"
          />
        </div>

        <div className="mt-4 flex items-start gap-3 p-3 rounded-xl bg-light-hover">
          <input
            id="force-update"
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            className="mt-0.5 w-4 h-4"
          />
          <label htmlFor="force-update" className="text-sm text-light-text cursor-pointer">
            <span className="font-medium">强制更新</span>
            <span className="block text-xs text-light-muted mt-0.5">
              勾选后，低于此版本的 App 打开会整屏拦住，不更新无法使用。慎用，通常只在大问题修复时开。
            </span>
          </label>
        </div>

        {force && (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 text-amber-700 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>强制更新会打断所有旧版本用户，发布前务必确认安装包已经上传成功。</span>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
          >
            <Save className="w-4 h-4" />
            {saving ? '保存中…' : '保存版本信息'}
          </button>
        </div>
      </div>

      <div className="card p-6">
        <h3 className="text-base font-semibold text-light-text flex items-center gap-2 mb-1">
          <Package className="w-4 h-4 text-accent-blue" />
          安装包
        </h3>
        <p className="text-sm text-light-muted mb-4">
          用户从这里下载。上传成功后立刻替换线上包，建议先传包再改版本号。
        </p>

        <div className="flex items-center gap-3 p-3 rounded-xl bg-light-hover text-sm mb-4">
          <span className="text-light-muted">当前包：</span>
          {state?.apk.exists ? (
            <>
              <span className="text-light-text font-medium">{formatBytes(state.apk.size)}</span>
              <span className="text-light-muted">
                {state.apk.updatedAt ? formatBeijingDateTime(state.apk.updatedAt) : ''}
              </span>
            </>
          ) : (
            <span className="text-red-500">还没有上传安装包</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm text-light-text file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-white file:text-light-text file:text-sm file:cursor-pointer"
          />
          <button
            onClick={() => void handleUpload()}
            disabled={!file || uploading}
            className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
          >
            <Upload className="w-4 h-4" />
            {uploading ? '上传中…' : '上传安装包'}
          </button>
        </div>

        {file && (
          <p className="text-xs text-light-muted mt-2">
            已选择 {file.name}（{formatBytes(file.size)}）
          </p>
        )}
      </div>
    </div>
  );
}
